import {
  CheckCircleIcon,
  DocumentCheckIcon,
  PlusIcon,
  ShieldCheckIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { Modal } from 'antd';
import { PageHeader, TWButton } from '../../components/ui';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import termsApi from '../../services/termsApi';
import './terms-and-conditions.css';

const DOCUMENTS = [
  {
    key: 'terms',
    label: 'Terms & Conditions',
    description: 'End-user terms presented at signup and checkout.',
    icon: DocumentCheckIcon,
    gradient: 'from-sky-500 to-blue-600',
    chipBg: 'bg-sky-50 dark:bg-sky-500/10',
    chipText: 'text-sky-700 dark:text-sky-300',
    placeholder: "e.g. 'Terms & Conditions'",
  },
  {
    key: 'privacy',
    label: 'Privacy Policy',
    description: 'How customer data is collected, used and protected.',
    icon: ShieldCheckIcon,
    gradient: 'from-emerald-500 to-teal-600',
    chipBg: 'bg-emerald-50 dark:bg-emerald-500/10',
    chipText: 'text-emerald-700 dark:text-emerald-300',
    placeholder: "e.g. 'Privacy Policy'",
  },
  {
    key: 'merchant',
    label: 'Merchant Agreement',
    description: 'Contract that merchants accept to operate on the platform.',
    icon: UsersIcon,
    gradient: 'from-violet-500 to-indigo-600',
    chipBg: 'bg-violet-50 dark:bg-violet-500/10',
    chipText: 'text-violet-700 dark:text-violet-300',
    placeholder: "e.g. 'Merchant Agreement'",
  },
];

const EMPTY_FORM = { id: '', name: '', description: '', version: 0 };

const isQuillEmpty = (html) => {
  if (!html) return true;
  const stripped = html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, '')
    .trim();
  return stripped.length === 0;
};

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link', 'blockquote', 'code-block'],
    ['clean'],
  ],
};

const TermsAndConditions = () => {
  const navigate = useNavigate();
  const [activeKey, setActiveKey] = useState('terms');
  const [loading, setLoading] = useState({
    listLoading: true,
    terms: false,
    privacy: false,
    merchant: false,
  });
  const [forms, setForms] = useState({
    terms: { ...EMPTY_FORM, name: 'terms' },
    privacy: { ...EMPTY_FORM, name: 'privacy' },
    merchant: { ...EMPTY_FORM, name: 'merchant' },
  });
  const [errors, setErrors] = useState({
    terms: { name: false, description: false },
    privacy: { name: false, description: false },
    merchant: { name: false, description: false },
  });

  const getTermsList = async () => {
    setLoading((prev) => ({ ...prev, listLoading: true }));
    try {
      const list = await termsApi.list({ PageNumber: 1, PageSize: 100 });
      const data = list.data?.data ?? [];
      const pickLatest = (name) =>
        data
          .filter((x) => x.name === name)
          .sort((a, b) => (b.version ?? 0) - (a.version ?? 0))[0];

      setForms({
        terms: pickLatest('terms')
          ? {
              id: pickLatest('terms').id,
              name: pickLatest('terms').name,
              description: pickLatest('terms').description,
              version: pickLatest('terms').version ?? 0,
            }
          : { ...EMPTY_FORM, name: 'terms' },
        privacy: pickLatest('privacy')
          ? {
              id: pickLatest('privacy').id,
              name: pickLatest('privacy').name,
              description: pickLatest('privacy').description,
              version: pickLatest('privacy').version ?? 0,
            }
          : { ...EMPTY_FORM, name: 'privacy' },
        merchant: pickLatest('merchant')
          ? {
              id: pickLatest('merchant').id,
              name: pickLatest('merchant').name,
              description: pickLatest('merchant').description,
              version: pickLatest('merchant').version ?? 0,
            }
          : { ...EMPTY_FORM, name: 'merchant' },
      });
    } catch (error) {
      // keep defaults
    } finally {
      setLoading((prev) => ({ ...prev, listLoading: false }));
    }
  };

  useEffect(() => {
    getTermsList();
  }, []);

  const onFormChange = (key, field, value) => {
    setForms((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
    if (errors[key]?.[field]) {
      setErrors((prev) => ({
        ...prev,
        [key]: { ...prev[key], [field]: false },
      }));
    }
  };

  const onConfirmNewVersion = (key) => {
    Modal.confirm({
      title: 'Create a new version?',
      content:
        'This clears the current form and prepares a fresh version to publish.',
      okText: 'Yes, create',
      cancelText: 'Cancel',
      okButtonProps: {
        style: {
          backgroundColor: '#2563eb',
          borderColor: '#2563eb',
          color: '#fff',
        },
      },
      cancelButtonProps: { style: { color: '#334155' } },
      onOk: () => {
        setForms((prev) => ({
          ...prev,
          [key]: { ...EMPTY_FORM, name: key },
        }));
        setErrors((prev) => ({
          ...prev,
          [key]: { name: false, description: false },
        }));
      },
    });
  };

  const onFormSubmit = async (key) => {
    const form = forms[key];
    const nameErr = !form.name?.trim();
    const descErr = isQuillEmpty(form.description);
    if (nameErr || descErr) {
      setErrors((prev) => ({
        ...prev,
        [key]: { name: nameErr, description: descErr },
      }));
      return;
    }

    setErrors((prev) => ({
      ...prev,
      [key]: { name: false, description: false },
    }));
    setLoading((prev) => ({ ...prev, [key]: true }));

    try {
      await termsApi.upsert({
        id: form.id,
        name: form.name,
        description: form.description,
      });
    } catch (error) {
      // swallow to match existing behavior
    } finally {
      await getTermsList();
      setLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const active = useMemo(
    () => DOCUMENTS.find((d) => d.key === activeKey) || DOCUMENTS[0],
    [activeKey],
  );
  const activeForm = forms[active.key];
  const activeErrors = errors[active.key];
  const ActiveIcon = active.icon;
  const isSaving = loading[active.key];

  return (
    <div className="terms-shell min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8">
      <div className="mx-auto w-full max-w-7xl space-y-6">
        {/* Header */}
        <PageHeader
          title="Legal Documents"
          description="Manage Terms, Privacy and Merchant agreements displayed across the platform."
          actions={
            <TWButton
              icon={CheckCircleIcon}
              label="Acceptances"
              onClick={() => navigate('/dashboard/settings/term-acceptances')}
            />
          }
        />

        {/* Document tabs */}
        <div className="reveal grid grid-cols-1 gap-3 sm:grid-cols-3">
          {DOCUMENTS.map((doc) => {
            const isActive = doc.key === activeKey;
            const DocIcon = doc.icon;
            const form = forms[doc.key];
            const published = !!form.id;
            return (
              <button
                key={doc.key}
                type="button"
                onClick={() => setActiveKey(doc.key)}
                className={`group flex items-start gap-3 rounded-md border p-4 text-left transition ${
                  isActive
                    ? 'border-blue-500 bg-white shadow-md ring-2 ring-blue-500/20 dark:border-sky-400 dark:bg-slate-800 dark:ring-sky-400/20'
                    : 'border-slate-200 bg-white/60 hover:border-slate-300 hover:bg-white dark:border-slate-700 dark:bg-slate-800/60 dark:hover:border-slate-600 dark:hover:bg-slate-800'
                }`}
              >
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-gradient-to-br ${doc.gradient} text-white shadow-sm`}
                >
                  <DocIcon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                      {doc.label}
                    </p>
                    {published ? (
                      <span className="inline-flex h-5 items-center gap-1 rounded-md bg-emerald-50 px-2 text-[0.65rem] font-semibold uppercase tracking-wide text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                        Live
                      </span>
                    ) : (
                      <span className="inline-flex h-5 items-center gap-1 rounded-md bg-slate-100 px-2 text-[0.65rem] font-semibold uppercase tracking-wide text-slate-500 dark:bg-slate-700/60 dark:text-slate-300">
                        Draft
                      </span>
                    )}
                  </div>
                  <p className="mt-1 line-clamp-2 text-xs text-slate-500 dark:text-slate-400">
                    {doc.description}
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Active document editor */}
        <div className="reveal overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
          {loading.listLoading ? (
            <div className="space-y-4 p-6">
              <div className="skeleton-bar h-5 w-40 rounded" />
              <div className="skeleton-bar h-10 w-full rounded-md" />
              <div className="skeleton-bar h-64 w-full rounded-md" />
              <div className="flex justify-end">
                <div className="skeleton-bar h-10 w-32 rounded-md" />
              </div>
            </div>
          ) : (
            <>
              {/* Editor header */}
              <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50/60 px-6 py-4 dark:border-slate-700 dark:bg-slate-900/40 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-gradient-to-br ${active.gradient} text-white shadow-sm`}
                  >
                    <ActiveIcon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                        {active.label}
                      </h2>
                      {activeForm.id && activeForm.version > 0 && (
                        <span
                          className={`inline-flex h-5 items-center gap-1 rounded-full px-2 text-[0.65rem] font-semibold uppercase tracking-wide ${active.chipBg} ${active.chipText}`}
                        >
                          v{activeForm.version}
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                      {active.description}
                    </p>
                  </div>
                </div>
                <TWButton
                  variant="default"
                  icon={PlusIcon}
                  label="New Version"
                  onClick={() => onConfirmNewVersion(active.key)}
                />
              </div>

              {/* Editor body */}
              <div className="space-y-5 px-6 py-6">
                <div>
                  <label
                    htmlFor={`name-${active.key}`}
                    className="block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400"
                  >
                    Name
                  </label>
                  <input
                    id={`name-${active.key}`}
                    type="text"
                    value={activeForm.name}
                    onChange={(e) =>
                      onFormChange(active.key, 'name', e.target.value)
                    }
                    placeholder={active.placeholder}
                    className={`mt-2 block w-full rounded-md border bg-white px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 dark:bg-slate-900/70 dark:text-slate-200 dark:placeholder:text-slate-500 ${
                      activeErrors.name
                        ? 'border-rose-400 focus:border-rose-500 focus:ring-rose-500/20 dark:border-rose-500/70'
                        : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500/20 dark:border-slate-600 dark:focus:border-sky-400 dark:focus:ring-sky-400/20'
                    }`}
                  />
                  {activeErrors.name && (
                    <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">
                      Name can not be empty
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Content
                  </label>
                  <div
                    className={`terms-editor mt-2 rounded-md ${
                      activeErrors.description ? 'ring-2 ring-rose-500/30' : ''
                    }`}
                  >
                    <ReactQuill
                      key={active.key}
                      theme="snow"
                      modules={QUILL_MODULES}
                      value={activeForm.description}
                      onChange={(val) =>
                        onFormChange(active.key, 'description', val)
                      }
                      placeholder="Write the document content…"
                    />
                  </div>
                  {activeErrors.description && (
                    <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">
                      Content can not be empty
                    </p>
                  )}
                </div>
              </div>

              {/* Editor footer */}
              <div className="flex items-center justify-between gap-3 border-t border-slate-200 bg-slate-50/60 px-6 py-4 dark:border-slate-700 dark:bg-slate-900/40">
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {activeForm.id
                    ? 'Updating publishes a new version to all customers.'
                    : 'Saving publishes the first version of this document.'}
                </p>
                <TWButton
                  variant="primary"
                  label={activeForm.id ? 'Update' : 'Save'}
                  disabled={isSaving}
                  loading={isSaving}
                  onClick={() => onFormSubmit(active.key)}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
